
const STORAGE_KEY = "rego:chunk-reload-at";
const COOLDOWN_MS = 60_000; // don't auto-reload again within 60s of the last attempt

// In-memory: true only for the page instance that just called reload().
let reloadInFlight = false;

function lastReloadAt(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  } catch {
    // Can't confirm timing - fail closed (pretend we "just" reloaded) so a
    // broken storage backend can't produce a tight reload loop.
    return Date.now();
  }
}

function recordReloadNow(now: number): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    // Ignore - the in-memory guard still covers this one page instance.
  }
}

/** Wire up the recovery listener. Call once, as early as possible in main.tsx. */
export function installChunkReloadRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();

    if (reloadInFlight) return; // duplicate error from the same failure burst

    const now = Date.now();
    const last = lastReloadAt();
    if (last !== null && now - last < COOLDOWN_MS) {
      console.error("[chunk-reload] chunk failed again shortly after a reload; giving up automatically.", event);
      return;
    }

    reloadInFlight = true;
    recordReloadNow(now);
    window.location.reload(); // reloads the exact current URL: path, query, and hash included
  });
}

/** True only in the page instance that just triggered the auto-reload. */
export function isChunkReloadInFlight(): boolean {
  return reloadInFlight;
}

/** True if a reload happened recently enough that we won't auto-retry again yet. */
export function hasExhaustedChunkReload(): boolean {
  const last = lastReloadAt();
  return last !== null && Date.now() - last < COOLDOWN_MS;
}

/** For a manual "Reload" button - a deliberate user retry bypasses the cooldown. */
export function resetChunkReloadGuard(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}
